define((require) => {
    "use strict";

    const {isUndefined, isArray, isEmpty}   = require("underscore");
    const Class                             = require("shared/util/Class");
    const SalesforceCommerce                = require("ui/experience/widgets/salesforceCommerceWidget/service/SalesforceCommerceConnect");
    const SalesforceCommerceDataAdapter     = require("ui/experience/widgets/salesforceCommerceWidget/service/v15/SalesforceCommerceDataAdapter");
    const {Logger}                            = require("shared/util/Logger");
    const Preconditions                     = require("shared/util/Preconditions");
    const Promise                           = require("shared/util/Promise");
    const {trim}                            = require("shared/util/StringUtils");
    const UrlUtil                           = require("shared/util/UrlUtil");

    /**
     * The full documentation of all endpoints can be find in https://info.salesforcecommerce.com/DOC1/index.jsp. <p>
     * Terms used:<p>
     * <ul>
     *  <li>OCAPI - SalesforceCommerce Open Commerce Api.
     * </ul>
     */

    var /**
         * Endpoint responsible to allow the user to search product in OCAPI, but only partial information.
         * @type {String}
         */
        PRODUCTS_SEARCH = "product_search",

        log = new Logger("SalesforceCommerceService"),

        /**
         * Endpoint responsible to allow the user to retrieve the full information of the Product in the OCAPI.
         * @type {String}
         */
        PRODUCT_SEARCH_BY_ID = "products";

    /**
     *  @class {SalesforceCommerceOCAPIService}
     *  A <i>SalesforceCommerce service</i> will be responsible to communicate with the OCAPI.
     *
     *  @author Pedro Augusto / <a href="http://www.zmags.com">Zmags</a>
     */
    var SalesforceCommerceOCAPIService = Class.extend("SalesforceCommerceOCAPIService", {

        /**
         * Search SalesforceCommerce products using the SalesforceCommerce web service.
         * <ol>
         *  <li> Call the product_search endpoint to look for products matching the <tt>query</tt> criteria.
         *  <li> Once this function has the productIDs matching the <tt>query</tt>, It must to retrieve the product
         *   information like image, description, price and brand. For do it, we call the endpoint products.
         * </ol>
         *
         * @param  {String} query word that will be searched in the SalesforceCommerce service.
         * @param  {String} siteId the Id of the SalesforceCommerce site in which the products are searched.
         *
         * @return {Object} The search results.
         */
        search: function(query, siteId) {
            Preconditions.checkArgument(query);

            // We only left and right trim the query.
            query = trim(query);

            var self = this,
                deferred = Promise.deferred(),
                standardQuery = query,
                wildcardQuery = "*" + query + "*";

            /**
             * Returns promise with products full descriptions.
             * @param {Object} data Products found by the getProductsHits function.
             * @param {String} siteId The SalesforceCommerce Site ID.
             * @returns {Promise} A promise with the product information results.
             * @private
             */
            function _getProductInfoPromise(data, siteId) {
                var ids = SalesforceCommerceDataAdapter.searchHits(data);

                log.info("Products found: " + ids.length);

                // Time to retrieve the product full information, taking the siteId into account.
                return self.getProductByIds(ids, siteId).done((data) => {
                    // finally, we have a result.
                    // Let's return it using the resolve from the begin.
                    return deferred.resolve(data);
                }).fail(deferred.reject);
            }

            /*
               The following strategy is a bit greedy but solves a search edge case (MOS-2415).
               If wildcard query approach does not find any hit, we will try with the standard query approach.
               The reason is because product search can be configured on SalesforceCommerce backend, and product search
               on our sandbox on specific products(e.g: canon-powershot-sd990-is) did not return any result
               with the wildcard approach.
            */
            log.info("Searching the word", wildcardQuery);
            return this._getProductsHits(wildcardQuery, siteId)
                .then((data) => {
                    return _getProductInfoPromise(data, siteId);
                })
                .then((data) => {
                    // we found a result, so we stop the chain and return the result set.
                    if (data && data.length) {
                        return deferred.resolve(data);
                    }

                    log.info("No results found, instead searching the word", standardQuery);
                    return self._getProductsHits(standardQuery, siteId)
                        .then((data) => {
                            return _getProductInfoPromise(data, siteId);
                        }).fail(deferred.reject);
                })
                .fail(deferred.reject);
        },

        /**
         * Get products full descriptions according with them ids. If the
         * user pass one string with an id it will return a SalesforceCommerceProductView else
         * it will return an SalesforceCommerceProductCollectionView.
         *
         * @param {String[]} ids
         * @param {String} siteId the Id of the SalesforceCommerce site in which the products are searched.
         * @return {Promise} A promise that will resolve with the product ids.
         */
        getProductByIds: function(ids, siteId) {
            var isCollection = isArray(ids);

            if (isUndefined(ids) || (isCollection && isEmpty(ids))) {
                return Promise.deferred().resolve();
            }

            var idsCriteria = isCollection ? "(" +  ids.join(",") + ")" : ids;

            return SalesforceCommerce.callRestApi({
                method: "product-by-id",
                args: ids,
                siteId: siteId,
                // Safely concat the ids with the search criteria.
                url: UrlUtil.concat(PRODUCT_SEARCH_BY_ID, idsCriteria),
                resultHandler: function(data) {
                    if (isCollection) {
                        return SalesforceCommerceDataAdapter.productsToCollection(data, SalesforceCommerce.getSettings());
                    }

                    return SalesforceCommerceDataAdapter.productToModel(data, SalesforceCommerce.getSettings());
                }
            });
        },

        /**
         * @private
         *
         * Search for a word in the SalesforceCommerce OCAPI.
         *
         * @param {String} query Parameter with the word criteria.
         * @param {String} siteId the Id of the SalesforceCommerce site in which the products are searched.
         * @return {Promise} A promise that will resolve with the product hits.
         */
        _getProductsHits: function(query, siteId) {
            return SalesforceCommerce.callRestApi({
                method: "search",
                args: query,
                url: PRODUCTS_SEARCH,
                siteId: siteId,
                data: {q: query},
                resultHandler: function(data) {
                    return data;
                }
            });
        }

    });

    return new SalesforceCommerceOCAPIService();
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/salesforceCommerceWidget/service/SalesforceCommerceOCAPIService.js